import {
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Modal,
	RichText,
	Text,
	VideoPlayer
} from "@pamperedchef/design-system";
import { className } from "@/app/utilities/className";
import type { HeadingTag } from "@/app/utilities/types";

import { ProductCard, ProductCardProps } from "../../ProductCard";
import * as styles from "./CommunityBuzzModal.module.scss";

export interface CommunityBuzzModalProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	triggerElement?: React.ReactNode;
	theme?: "16:9" | "2:3";
	heading?: string;
	headingTag?: HeadingTag;
	description?: string;
	source?: string;
	image?: ImageProps<ImageComponentProps>;
	video?: string;
	productSectionTitle?: string;
	products?: ProductCardProps<LinkComponentProps, ImageComponentProps>[];
	cta?: LinkProps<LinkComponentProps>;
	onClose?: () => void;
}

/**
 * CommunityBuzzModal Component
 */
export function CommunityBuzzModal<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	triggerElement,
	heading,
	headingTag = "h1",
	theme = "16:9",
	description,
	source,
	image,
	productSectionTitle,
	products,
	cta,
	video,
	onClose
}: CommunityBuzzModalProps<
	LinkComponentProps,
	ImageComponentProps
>): JSX.Element {
	const isFullLayout = theme === "16:9";

	return (
		<Modal
			triggerElement={triggerElement}
			className={styles["community-buzz-modal"]}
			onClose={onClose}
		>
			{isFullLayout ? (
				<>
					{heading && (
						<Heading tag={headingTag} styledAs="h2">
							{heading}
						</Heading>
					)}

					{description && (
						<RichText
							className={
								styles["community-buzz-modal__description"]
							}
							content={description}
						/>
					)}
					{source && (
						<Text
							className={styles["community-buzz-modal__source"]}
							styledAs="xsr"
						>
							{source}
						</Text>
					)}
					{image && !video && (
						// @ts-expect-error - React 19 generic type limitation with Image component
						<Image<ImageComponentProps>
							{...image}
							objectFit="cover"
							aspectRatio="16:9"
							alt={image.alt || "Community Buzz Modal Image"}
						/>
					)}
					{video && (
						<VideoPlayer
							videoSrc={video}
							posterSrc={image?.src}
							videoClassName={
								styles["community-buzz-modal__video"]
							}
						/>
					)}

					<div className={styles["community-buzz-modal__products"]}>
						{productSectionTitle && (
							<Text
								className={
									styles[
										"community-buzz-modal__products__title"
									]
								}
								styledAs="eyebrow-1"
							>
								{productSectionTitle}
							</Text>
						)}

						<div
							{...className(
								"grid",
								styles["community-buzz-modal__products__list"]
							)}
						>
							{products?.map(
								(product, index) =>
									product?.name && (
										<div
											key={`${product.name}-${index}`}
											className="col-xs-6"
										>
											<ProductCard {...product} />
										</div>
									)
							)}
						</div>
					</div>
				</>
			) : (
				<div className={styles["community-buzz-modal__column-layout"]}>
					<div
						className={
							styles["community-buzz-modal__column-layout__left"]
						}
					>
						{heading && (
							<Heading tag={headingTag} styledAs="h2">
								{heading}
							</Heading>
						)}

						{description && (
							<RichText
								className={
									styles["community-buzz-modal__description"]
								}
								content={description}
							/>
						)}
						{source && (
							<Text
								className={
									styles["community-buzz-modal__source"]
								}
								styledAs="xsr"
							>
								{source}
							</Text>
						)}
						<div
							className={styles["community-buzz-modal__products"]}
						>
							{productSectionTitle && (
								<Text
									className={
										styles[
											"community-buzz-modal__products__title"
										]
									}
									styledAs="eyebrow-1"
								>
									{productSectionTitle}
								</Text>
							)}

							<div
								{...className(
									"grid",
									styles[
										"community-buzz-modal__products__list"
									],
									styles[
										"community-buzz-modal__products__list--desktop"
									]
								)}
							>
								{products?.map(
									(product, index) =>
										product?.name && (
											<div
												key={`${product.name}-${index}`}
												className="col-xs-6 col-lg-12"
											>
												<ProductCard {...product} />
											</div>
										)
								)}
							</div>
						</div>
					</div>
					<div
						className={
							styles["community-buzz-modal__column-layout__right"]
						}
					>
						{image && !video && (
							// @ts-expect-error - React 19 generic type limitation with Image component
							<Image<ImageComponentProps>
								{...image}
								objectFit="cover"
								aspectRatio="2:3"
								alt={image.alt || "Community Buzz Modal Image"}
							/>
						)}
						{video && (
							<VideoPlayer
								videoSrc={video}
								posterSrc={image?.src}
								videoClassName={
									styles["community-buzz-modal__video-full"]
								}
							/>
						)}
					</div>

					<div
						{...className(
							"grid",
							styles["community-buzz-modal__products__list"],
							styles[
								"community-buzz-modal__products__list--mobile"
							]
						)}
					>
						{products?.map(
							(product, index) =>
								product?.name && (
									<div
										key={`${product.name}-${index}`}
										className="col-xs-6 col-lg-12"
									>
										<ProductCard {...product} />
									</div>
								)
						)}
					</div>
				</div>
			)}

			{cta?.href && (
				<Link<LinkComponentProps>
					{...cta}
					className={styles["community-buzz-modal__cta"]}
					kind="secondary"
				/>
			)}
		</Modal>
	);
}
